import { getClassroomDetails } from "js/api/classApi";
import { getUserDetail, searchUsers } from "js/api/usersApi";
import ClassroomSessionScheduler from "js/components/ClassroomSessionSchedule";
import { ClassroomInviteCode } from "js/components/InviteCode/InviteCode";
import useStore from "stores/index";
import { getFeatures } from "stores/classwizeConfigStore";
import { compareChangesInArrays } from "js/utils/arrayUtils";
import { getUserDisplayTextForSelect } from "js/utils/userUtils";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { CLASSROOM_SOURCE_TYPES, CLASSROOM_TYPES } from "types/classrooms";
import shallow from "zustand/shallow";
import * as clsApi from "../../api/classroomApi";
import { useLicenseStore } from "stores/v2/licenseStore";
import { EDIT_CLASSROOM_ACTION } from "../ClassroomTileV2";
import ErrorBoundary from "../ErrorBoundary";
import { DangerButton } from "../shared/button/DangerButton";
import { PrimaryButton } from "../shared/button/PrimaryButton";
import { DeleteClassConfirmModal } from "@/components/DeleteClassConfirmModal";
import StandardModalV2 from "../shared/StandardModalV2";
import { renderTeacherTab } from "./teacherTab";
import { Box, Button } from "@chakra-ui/react";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { envConfig } from "@/config";
import { getClassroomDisplayName } from "@/utils/classroomUtils";

/**
 * @typedef {Object} ClassroomEditV2Props
 * @property {Object} classroom - The classroom object containing details about the classroom.
 * @property {boolean} open - Indicates whether the modal is open.
 * @property {Function} handleAction - Callback function to handle actions like save, cancel, or delete.
 */

/**
 * @type {React.FC<ClassroomEditV2Props>}
 * @returns {JSX.Element}
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const ClassroomEditV2 = (props) => {
	const [features, setFeatures] = useState(getFeatures(useStore.getState()));
	const [hasError, setHasError] = useState(false);
	const [inputError, setInputError] = useState(false);
	const [isDeleteOpen, setDeleteOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isInviteCodeEnabled, setInviteCodeEnabled] = useState(!!props.classroom.session?.inviteCode);

	const [students, setStudents] = useState([]);
	const [stagedStudents, setStagedStudents] = useState([]);
	const [teachers, setTeachers] = useState([]);
	const [stagedTeachers, setStagedTeachers] = useState([]);
	const [schedule, setSchedule] = useState([]);
	const [stagedSchedule, setStagedSchedule] = useState([]);
	const [stagedInvitation, setStagedInvitation] = useState({
		code: props.classroom.session?.inviteCode,
		expiry: props.classroom.session?.inviteCodeExpiry,
	});
	const [teacherError, setTeacherError] = useState();
	const [lastSelectedTeacher, setLastSelectedTeacher] = useState();

	const { classroom } = useLicenseStore();

	const TABS = useMemo(() => {
		const result = {
			// Tabs that are always shown, regardless of licensed products
			teacher: "Teachers",
		};
		if (classroom) {
			// Tabs that are shown only if Classroom Management is licensed
			result.invitation = "Invitation Code";
		}
		if (features.indexOf("schedules") > -1) {
			result.schedule = "Scheduling";
		}
		return result;
	}, [features, classroom]);

	const [activeTab, setActiveTab] = useState(TABS.teacher);

	useEffect(() => {
		const unsubClasswizeConfig = useStore.subscribe(
			(state) => [state.classwizeConfig.data],
			() => setFeatures(getFeatures(useStore.getState())),
			shallow
		);

		return () => {
			unsubClasswizeConfig();
		};
	}, []);

	useEffect(() => {
		getClassroomDetails(
			props.classroom.id || props.classroom.name,
			true,
			true,
			true,
			false,
			props.classroom.type,
			props.classroom.sourceId,
			useUserSessionStore.getState().getTeacherId()
		).then((classroomDetail) => {
			const { classroom } = classroomDetail;
			useStore.getState().classroom.updateClassroomDataV2(classroomDetail);
			setStudents(classroom.students || []);
			setStagedStudents(classroom.students || []);
			setTeachers(classroom.teachers || []);
			setStagedTeachers(classroom.teachers || []);
			setSchedule(classroom.schedule.periods);
			setStagedSchedule(classroom.schedule.periods);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderActions = () => {
		const renderDelete = () => {
			const { classroom } = props;
			const sourceType = classroom.sourceType.toLowerCase();
			if (classroom.type.toLowerCase() === CLASSROOM_TYPES.CLASSROOM && sourceType.toLowerCase() === CLASSROOM_SOURCE_TYPES.LOCAL) {
				return (
					<DangerButton
						className="mui-flat-button"
						label="Delete class"
						primary={false}
						secondary={true}
						onClick={() => handleAction(EDIT_CLASSROOM_ACTION.delete)}
						data-cy="delete"
					/>
				);
			}

			return <span />;
		};

		const maxStudents = envConfig.MAX_STUDENTS;
		const renderSave = () => {
			if (isSaving) {
				return (
					<ErrorBoundary>
						<PrimaryButton className="mui-flat-button" label="" primary={true}>
							<i className="fa fa-refresh fa-spin fa-fw" />
						</PrimaryButton>
					</ErrorBoundary>
				);
			}
			const isButtonDisabled = !!hasError || inputError || stagedStudents?.length > maxStudents;
			return (
				<Button label="Save" onClick={() => handleAction(EDIT_CLASSROOM_ACTION.save)} data-cy="save" isDisabled={isButtonDisabled}>
					Save
				</Button>
			);
		};

		return (
			<div className="ClassroomEdit_modalButtons">
				{renderDelete()}
				{renderSave()}
			</div>
		);
	};

	const handleSave = async (event) => {
		if (!hasChanged(stagedStudents, students) && !hasChanged(stagedTeachers, teachers) && !hasChanged(stagedSchedule, schedule)) {
			props.handleAction(EDIT_CLASSROOM_ACTION.cancel);
		}

		setIsSaving(true);
		if (event) event.preventDefault();

		const { classroom } = props;
		const updatedClassroom = {
			group: classroom.sourceId,
			groupId: classroom.id,
			periods: stagedSchedule,
			classroomUsers: stagedStudents.map((user) => user.username),
			classroomUserIds: stagedStudents.map((user) => user.id),
			classroomOwners: stagedTeachers.map((user) => user.username),
			classroomOwnerIds: stagedTeachers.map((user) => user.id),
			invite_code: stagedInvitation?.code,
			invite_code_expiry: stagedInvitation?.expiry,
			is_monitoring_class: classroom.session?.isMonitoring,
		};

		updateClassroomDetails(classroom.type, updatedClassroom);
	};

	const isClassroomValid = async (classroomType, updatedClassroom) => {
		if (!classroomType) {
			setHasError("Classroom type is required");
			return false;
		}

		if (!updatedClassroom) {
			setHasError("Modified classroom is required");
			return false;
		}

		const schedules = updatedClassroom?.periods;
		if (schedules && schedules.length > 0) {
			const isScheduleValid = !schedules.some((schedule) => schedule?.startTime?.length === 0 || schedule?.endTime?.length === 0);
			if (!isScheduleValid) {
				setHasError("Classroom schedule is not valid, please check if time or day is empty");
				return false;
			}
		}

		setHasError(false);
		return true;
	};

	const updateClassroomDetails = async (classroomType, updatedClassroom) => {
		if (isClassroomValid(classroomType, updatedClassroom)) {
			updatedClassroom = {
				...updatedClassroom,
				policy_id: props.classroom.stableIdV1,
			};
			const endpoint = classroomType.toLowerCase() === CLASSROOM_TYPES.CLASSROOM ? "classrooms/" : "groups/";
			await clsApi
				.Post(
					endpoint + updatedClassroom.groupId,
					updatedClassroom,
					() => {
						useStore.getState().classroom.updateClassroomDataV2(updatedClassroom, true);
						handleAction(EDIT_CLASSROOM_ACTION.saveComplete);
						setHasError(false);
					},
					(error) => {
						setHasError(error);
					}
				)
				.finally(() => {
					setIsSaving(false);
				});
		}
	};

	const handleAction = async (action) => {
		switch (action) {
			case EDIT_CLASSROOM_ACTION.cancel:
			case EDIT_CLASSROOM_ACTION.saveComplete:
				setDeleteOpen(false);
				props.handleAction(action);
				break;
			case EDIT_CLASSROOM_ACTION.delete:
				setDeleteOpen(true);
				break;
			case EDIT_CLASSROOM_ACTION.save:
				setDeleteOpen(false);
				handleSave();
				break;
			case EDIT_CLASSROOM_ACTION.deleteConfirmed:
				setDeleteOpen(false);
				props.handleAction(EDIT_CLASSROOM_ACTION.delete);
				break;
			default:
				break;
		}
	};

	const handleSwitchTab = (tab) => {
		setActiveTab(tab);
	};

	const getTabClassname = (tab) => {
		if (activeTab === tab) {
			return "ClassroomEdit_modalTabButton--selected";
		}
		return "ClassroomEdit_modalTabButton";
	};

	const getTabContent = (showWarning = false) => {
		switch (activeTab) {
			case TABS.teacher: {
				return renderTeacherTab(
					showWarning,
					props.classroom.type.toLowerCase() === CLASSROOM_TYPES.GROUP,
					lastSelectedTeacher,
					teacherError,
					getUsersCb,
					stagedTeachers,
					handleAddClassroomOwnerCb,
					handleRemoveTeacher,
					stagedStudents.length
				);
			}
			case TABS.schedule:
				return renderScheduleContent();
			case TABS.invitation:
				return renderInvitationContent();
			default:
				break;
		}
	};

	const renderTitleSection = () => {
		return (
			<div className="ClassroomEdit_modalHeader">
				<div className="ClassroomEdit_modalTabContainer">
					{Object.entries(TABS).map(([key, value]) => {
						// Only show invitation tab if the classroom is LOCAL
						if (value === TABS.invitation && props.classroom.sourceType.toLowerCase() !== CLASSROOM_SOURCE_TYPES.LOCAL) {
							return <></>;
						}
						return (
							<div className={getTabClassname(value)} key={key} onClick={() => handleSwitchTab(value)}>
								{value}
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	const getUsersCb = async (filterStr) => {
		if (filterStr) {
			const students = await searchUsers(filterStr);
			return students.map((user) => ({
				text: getUserDisplayTextForSelect(user),
				value: user,
			}));
		}
		return [];
	};

	const handleAddClassroomOwner = async (owner) => setStagedTeachers([...stagedTeachers, owner]);

	const handleAddClassroomOwnerCb = async (user) => {
		//Get teacher details first
		try {
			//Check for duplicates
			if (stagedTeachers.some((previous) => user.username === previous.username)) {
				setTeacherError(getUserDisplayTextForSelect(user) + " is already a teacher of the class, please select a different user");
				return;
			}

			//Perform search
			const foundUser = await getUserDetail(user.username, false, false, true);
			setLastSelectedTeacher(foundUser);
			if (!foundUser) {
				setTeacherError(getUserDisplayTextForSelect(user) + " is not a valid teacher, please try a different user");
				return;
			}

			//First check if this is a teacher, otherwise bail
			if (!foundUser.isTeacher) {
				return;
			}

			handleAddClassroomOwner(user);
		} catch (error) {
			console.error("Error fetching user details", error);
			setTeacherError(getUserDisplayTextForSelect(user) + " is not a valid teacher, please try a different user");
		}
	};

	const handleRemoveTeacher = (user) => {
		const tmpClassroomTeachers = stagedTeachers.filter((teacher) => getUserDisplayTextForSelect(teacher) !== user);
		setStagedTeachers(tmpClassroomTeachers);
	};

	const renderScheduleContent = () => (
		<div>
			<ClassroomSessionScheduler
				periods={stagedSchedule}
				setSchedules={(schedule, error) => {
					setInputError(error);
					setStagedSchedule(schedule);
				}}
				studentCount={stagedStudents.length}
				onClassroomModifyError={hasError}
				onClearModifyError={() => setHasError(false)}
			/>
		</div>
	);

	const renderInvitationContent = () => (
		<div>
			<ClassroomInviteCode
				codeInviteEnabled={isInviteCodeEnabled}
				code={stagedInvitation?.code || props.classroom.session?.inviteCode}
				codeExpiry={stagedInvitation?.expiry || props.classroom.session?.inviteCodeExpiry}
				codeCallback={(enabled, code, expiry) => {
					setInviteCodeEnabled(enabled);
					setStagedInvitation({ code, expiry });
				}}
				studentCount={stagedStudents?.length}
			/>
		</div>
	);

	const hasChanged = (current, previous) => current.length && compareChangesInArrays(current, previous) > 0;

	const renderContentSection = () => {
		const { classroom } = props;
		const sourceType = classroom.sourceType.toLowerCase();
		if (
			sourceType.toLowerCase() === CLASSROOM_SOURCE_TYPES.GOOGLE ||
			sourceType.toLowerCase() === CLASSROOM_SOURCE_TYPES.CLEVER ||
			sourceType.toLowerCase() === CLASSROOM_SOURCE_TYPES.ONEROSTER ||
			sourceType.toLowerCase() === CLASSROOM_SOURCE_TYPES.CLASSLINK ||
			sourceType.toLowerCase() === CLASSROOM_SOURCE_TYPES.WONDE
		) {
			return getTabContent(true);
		} else if ((sourceType && sourceType.toLowerCase() === CLASSROOM_SOURCE_TYPES.LOCAL) || features.indexOf("add_user") > -1) {
			return getTabContent();
		} else {
			return renderLockedContentSection();
		}
	};

	const renderLockedContentSection = () =>
		renderLockedModalContent("Cannot edit students or teachers. This classroom has been automatically created by the administrator.");

	const renderLockedModalContent = (message) => {
		switch (activeTab) {
			case TABS.teacher:
				return <div>{message}</div>;
			case TABS.schedule:
				return renderScheduleContent();
			default:
				break;
		}
	};

	const renderModalContent = () => {
		const { classroom } = props;
		if (classroom) {
			return renderContentSection();
		}
	};

	const renderModalBody = () => {
		return (
			<div>
				{renderTitleSection()}
				<br />
				{renderModalContent()}
			</div>
		);
	};

	const getDescription = () => {
		const classroomName = props.classroom.type === "CLASSROOM" ? props.classroom.name : props.classroom.description;
		if (props.classroom?.personalisation?.displayName?.length > 0) {
			return classroomName;
		}
		return null;
	};

	return (
		<Box onClick={(event) => event.stopPropagation()}>
			<StandardModalV2
				title={`Class settings - ${getClassroomDisplayName(props.classroom)}`}
				description={getDescription()}
				actions={renderActions()}
				modal={true}
				open={props.open}
				close={() => handleAction(EDIT_CLASSROOM_ACTION.cancel)}
				bodyClass="classSettings_modal"
			>
				{renderModalBody()}
				<DeleteClassConfirmModal
					open={isDeleteOpen}
					classroomName={getClassroomDisplayName(props.classroom)}
					onDelete={() => handleAction(EDIT_CLASSROOM_ACTION.deleteConfirmed)}
					onCancel={() => setDeleteOpen(false)}
				/>
			</StandardModalV2>
		</Box>
	);
};

export default ClassroomEditV2;

ClassroomEditV2.propTypes = {
	classroom: PropTypes.object,
	open: PropTypes.bool,
	handleAction: PropTypes.func,
};
